import React, { useState, useEffect } from "react";
import WelcomeCard from "../../components/common/WelcomeCard";
import { PiArticleNyTimesBold } from "react-icons/pi";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import {
  FaThumbsUp,
  FaComments,
  FaShareAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import Loader from "../../components/common/Loader";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AddArticleModal from "../../components/modals/AddArticleModal";
import EditArticleModal from "../../components/modals/EditArticleModal";

const Articles = () => {
  const navigate = useNavigate();
  const [articleList, setArticleList] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showComments, setShowComments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const handleAddOpen = () => setOpenAddModal(true);
  const handleAddClose = () => setOpenAddModal(false);

  const handleEditOpen = (article) => {
    setSelectedArticle(article);
    setOpenEditModal(true);
  };
  const handleEditClose = () => setOpenEditModal(false);

  useEffect(() => {
    allArticles();
  }, []);

  const allArticles = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/articles/get-all-articles`)
      .then((response) => {
        setArticleList(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  };

  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this article?"))
      return;
    axios
      .delete(`${process.env.REACT_APP_API_URL}/articles/delete-articles/${id}`)
      .then(() => allArticles())
      .catch((error) => console.error(error));
  };

  const handleDeleteComment = async (articleId, commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/articles/${articleId}/comment/${commentId}`,
      );
      alert("Comment deleted successfully");
      allArticles();
    } catch (error) {
      console.error("Error deleting comment:", error);
      alert("Failed to delete comment");
    }
  };

  const handleLike = async (id) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/articles/${id}/like`);
      allArticles();
    } catch (error) {
      console.error("Like failed:", error);
    }
  };

  const handleShare = async (id) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/articles/${id}/share`);
      allArticles();
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  // ✅ Filter by search
  const filteredArticles = articleList.filter(
    (article) =>
      article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.author?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // ✅ Pagination calculated on filteredArticles
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredArticles.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  return (
    <>
      <AddArticleModal
        open={openAddModal}
        handleOpen={handleAddOpen}
        handleClose={handleAddClose}
        allArticles={allArticles}
      />

      {selectedArticle && (
        <EditArticleModal
          open={openEditModal}
          handleClose={handleEditClose}
          articleData={selectedArticle}
          reloadArticleDetails={allArticles}
        />
      )}

      <div className="p-4">
        <WelcomeCard />

        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold text-[#000000] tracking-wider">
            Articles Management
          </h1>

          {/* Search + Button in one div */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search by title or author..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:w-64 border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <button
              onClick={handleAddOpen}
              className="flex justify-center items-center bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white font-semibold py-2 px-4 rounded-md hover:bg-red-600 transition"
            >
              <PiArticleNyTimesBold size={24} className="mr-2" />
              Add New Article
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center border p-4 rounded">
            <Loader /> <span className="ml-2">Loading articles...</span>
          </div>
        ) : currentItems.length === 0 ? (
          <p className="text-center text-gray-500">No articles available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {currentItems.map((article, index) => (
              <div
                key={index}
                className="relative shadow-md rounded bg-white flex flex-col p-4"
              >
                <img
                  className="rounded-lg w-full h-48 object-cover cursor-pointer"
                  src={article?.images[0] || "https://via.placeholder.com/300"}
                  alt={article.title}
                  onClick={() => navigate(`/articles-details/${article._id}`)}
                />

                <div className="mt-4 flex flex-col justify-between flex-grow">
                  <div>
                    <h3 className="text-lg font-bold">{article.title}</h3>
                    <div className="text-sm text-gray-600 mb-2">
                      {article.author} • {article.createdAt?.split("T")[0]}
                    </div>
                    <div
                      className="text-gray-700 text-sm overflow-hidden"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                      dangerouslySetInnerHTML={{ __html: article.description }}
                    />
                  </div>

                  <div className="flex justify-between mt-4 text-gray-700 text-sm">
                    <div className="flex items-center space-x-2">
                      <FaThumbsUp className="text-blue-500" />
                      <span>{article.likes || 0}</span>
                    </div>
                    <div
                      className="flex items-center space-x-2 cursor-pointer hover:text-blue-700"
                      onClick={() =>
                        setShowComments(
                          showComments === article._id ? null : article._id,
                        )
                      }
                    >
                      <FaComments className="text-green-500" />
                      <span>{article.comments?.length || 0}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaShareAlt className="text-purple-500" />
                      <span>{article.shares || 0}</span>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4 mt-3">
                    <button
                      onClick={() => handleLike(article._id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Like
                    </button>
                    <button
                      onClick={() => handleShare(article._id)}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      Share
                    </button>
                    <button
                      onClick={() => handleEditOpen(article)}
                      className="text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <AiOutlineEdit size={20} /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(article._id)}
                      className="text-red-600 hover:text-red-800 flex items-center"
                    >
                      <AiOutlineDelete size={20} /> Delete
                    </button>
                  </div>

                  {showComments === article._id && (
                    <div className="mt-4 bg-gray-50 p-3 rounded-md border">
                      <h4 className="font-semibold mb-2">Comments</h4>
                      {article.comments?.length > 0 ? (
                        article.comments.map((comment) => (
                          <div
                            key={comment._id}
                            className="flex justify-between items-center border-b py-2"
                          >
                            <div>
                              <p className="font-medium text-sm">
                                {comment.name}
                              </p>
                              <p className="text-gray-600 text-sm">
                                {comment.text}
                              </p>
                            </div>
                            <button
                              onClick={() =>
                                handleDeleteComment(article._id, comment._id)
                              }
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">
                          No comments yet.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`p-2 rounded-md ${currentPage === 1 ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-orange-500 text-white hover:bg-orange-600"}`}
            >
              <FaChevronLeft />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 rounded-md ${currentPage === page ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-md ${currentPage === totalPages ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-orange-500 text-white hover:bg-orange-600"}`}
            >
              <FaChevronRight />
            </button>
          </div>
        )}

        {/* ✅ Page Info */}
        {filteredArticles.length > 0 && (
          <div className="text-center mt-4 text-gray-600">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredArticles.length)} of{" "}
            {filteredArticles.length} articles
          </div>
        )}
      </div>
    </>
  );
};

export default Articles;
